// ─────────────────────────────────────────────────────────
// CampoConSelector.tsx — Contasis Design System
// Control genérico: selector de tipo + input de monto + mínimo,
// todo dentro de una sola tarjeta. No decide reglas de negocio,
// solo dibuja el estado que recibe (ver useCampoConSelector).
// ─────────────────────────────────────────────────────────

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Tag, Pencil, Info, AlertTriangle } from 'lucide-react';
import Tooltip from '../Tooltip/Tooltip';
import type { CampoConSelectorDensity, CampoConSelectorOption, CampoConSelectorProps } from './types';

interface PopoverPosition {
  top: number;
  left: number;
  width: number;
  openUpward: boolean;
  visible: boolean;
}

const DENSITY_CLASSES: Record<CampoConSelectorDensity, { row: string; input: string; min: string }> = {
  compact: { row: 'py-[3px] px-2.5 min-h-[20px]', input: 'py-[3px] px-2.5 min-h-[22px]', min: 'py-[2px] px-2.5 pb-[3px] min-h-[14px]' },
  medium: { row: 'py-[5px] px-2.5 min-h-[24px]', input: 'py-[5px] px-2.5 min-h-[28px]', min: 'py-1 px-2.5 pb-[5px] min-h-[18px]' },
  extended: { row: 'py-[7px] px-2.5 min-h-[28px]', input: 'py-[7px] px-2.5 min-h-[32px]', min: 'py-1.5 px-2.5 pb-[7px] min-h-[22px]' },
};

function formatAmount(n: number): string {
  return n.toFixed(2);
}

export function CampoConSelector({
  value,
  onValueChange,
  options,
  selectedOptionId,
  confirmed,
  onSelectOption,
  density = 'medium',
  placeholder = '0.00',
  disabled = false,
  className = '',
  prefix,
  rawInputValue,
  onRawInputChange,
  onInputBlur,
  autoFillOnSelect = true,
  staticOptionLabel,
  errorMessage,
  manualEditBadge,
  showWillSaveBadge = true,
  previousValueLabel,
  fallbackMinValue,
}: CampoConSelectorProps) {
  const [open, setOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<PopoverPosition>({ top: -9999, left: -9999, width: 216, openUpward: false, visible: false });
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  const adjustPopoverPosition = useCallback(() => {
    if (!rootRef.current || !popoverRef.current) return;
    const triggerRect = rootRef.current.getBoundingClientRect();
    const popoverHeight = popoverRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const openUpward = spaceBelow < popoverHeight + 8;

    setPopoverPos({
      top: openUpward ? triggerRect.top - popoverHeight - 6 : triggerRect.bottom + 6,
      left: triggerRect.left,
      width: Math.max(triggerRect.width, 216),
      openUpward,
      visible: true,
    });
  }, []);

  const handleToggleOpen = useCallback(() => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    setPopoverPos({ top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, 216), openUpward: false, visible: false });
    setOpen((v) => !v);
  }, []);

  useLayoutEffect(() => {
    if (open) adjustPopoverPosition();
  }, [open, adjustPopoverPosition]);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        rootRef.current && !rootRef.current.contains(target) &&
        popoverRef.current && !popoverRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function handleScroll() {
      setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const hasOptions = options.length > 0;
  const selectedOption = options.find((o) => o.id === selectedOptionId) ?? null;
  const hasValue = value !== null && !Number.isNaN(value);
  const isPredefined = selectedOption?.listValue != null;
  const min = selectedOption?.minValue ?? fallbackMinValue ?? null;

  const isManualEdit =
    manualEditBadge ??
    (confirmed && isPredefined && hasValue && Math.abs((value as number) - (selectedOption!.listValue as number)) > 0.001);

  const willBeSaved = showWillSaveBadge && hasValue && !isPredefined;

  const isBelowMin = hasValue && min != null && (value as number) < min;
  const hasError = Boolean(errorMessage) || isBelowMin;

  const labelText = selectedOption ? selectedOption.label : 'Elegir tipo';
  const d = DENSITY_CLASSES[density];

  function handleInputChange(raw: string) {
    if (raw.trim() === '') {
      onValueChange?.(null);
      return;
    }
    const parsed = parseFloat(raw);
    onValueChange?.(Number.isNaN(parsed) ? null : parsed);
  }

  function handleOptionClick(option: CampoConSelectorOption) {
    onSelectOption(option.id);
    if (autoFillOnSelect && option.listValue != null) {
      onValueChange?.(option.listValue);
    }
    setOpen(false);
  }

  return (
    <div ref={rootRef} className={`relative w-[158px] ${className}`}>
      <div
        className={[
          'rounded-lg border bg-surface-0 overflow-hidden transition-colors',
          hasError ? 'border-error' : 'border-strong',
          'focus-within:ring-2 focus-within:ring-brand',
          hasError ? 'focus-within:ring-error-bg' : 'focus-within:border-focus',
        ].join(' ')}
      >
        {hasOptions ? (
          <button
            type="button"
            disabled={disabled}
            aria-expanded={open}
            aria-controls={popoverId}
            onClick={handleToggleOpen}
            className={[
              'flex w-full items-center gap-1.5 border-b bg-transparent text-left',
              'hover:bg-surface-hover',
              confirmed ? 'border-muted' : 'border-dashed border-muted',
              d.row,
            ].join(' ')}
          >
            <span
              className={[
                'truncate max-w-[96px] text-label font-semibold leading-none',
                confirmed ? 'text-brand' : 'text-muted font-medium',
              ].join(' ')}
              title={labelText}
            >
              {labelText}
            </span>
            <ChevronDown
              size={11}
              className={['ml-auto shrink-0 text-muted transition-transform', open ? 'rotate-180' : ''].join(' ')}
            />
          </button>
        ) : staticOptionLabel ? (
          <div className={['flex w-full items-center border-b border-muted bg-transparent', d.row].join(' ')}>
            <span className="truncate max-w-full text-label font-semibold leading-none text-tertiary" title={staticOptionLabel}>
              {staticOptionLabel}
            </span>
          </div>
        ) : null}

        <div className={['flex items-center gap-1.5', d.input].join(' ')}>
          {prefix && <span className="text-[11.5px] text-muted leading-none shrink-0">{prefix}</span>}
          <input
            type="text"
            inputMode="decimal"
            disabled={disabled}
            placeholder={placeholder}
            value={rawInputValue ?? (hasValue ? formatAmount(value as number) : '')}
            onChange={(e) => (onRawInputChange ? onRawInputChange(e.target.value) : handleInputChange(e.target.value))}
            onBlur={(e) => onInputBlur?.(e.target.value)}
            className="w-full min-w-0 bg-transparent text-[13.5px] font-bold leading-none text-primary outline-none placeholder:text-disabled placeholder:font-semibold"
          />

          {isManualEdit && (
            <Tooltip
              text={`Precio editado manualmente solo para este comprobante. Tu ${selectedOption?.label ?? staticOptionLabel ?? ''} guardado no cambia.`}
              position="top"
              size="sm"
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-warning-bg text-warning-hover">
                <Pencil size={9} />
              </span>
            </Tooltip>
          )}

          {willBeSaved && (
            <Tooltip
              text={
                selectedOption?.label ?? staticOptionLabel
                  ? `Este valor se guardará como tu precio ${selectedOption?.label ?? staticOptionLabel} al emitir.`
                  : 'Este valor se guardará como tu PRECIO BASE al emitir.'
              }
              position="top"
              size="sm"
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-info-bg text-info-hover">
                <Tag size={9} />
              </span>
            </Tooltip>
          )}
        </div>

        {previousValueLabel && (
          <div className={['flex justify-end border-t border-muted', d.min].join(' ')}>
            <span className="font-mono text-[10.5px] leading-none text-disabled line-through">{previousValueLabel}</span>
          </div>
        )}

        {min != null && (
          <div
            className={[
              'flex items-center gap-1 border-t',
              hasError ? 'border-error-border bg-error-bg' : 'border-muted',
              d.min,
            ].join(' ')}
          >
            {hasError ? (
              <AlertTriangle size={10.5} className="shrink-0 text-error" />
            ) : (
              <Info size={10.5} className="shrink-0 text-muted" />
            )}
            <span className={['truncate text-[10.5px] font-medium leading-tight', hasError ? 'text-error font-semibold' : 'text-muted'].join(' ')}>
              {errorMessage || (isBelowMin ? `Bajo el mínimo (${prefix ?? ''} ${formatAmount(min)})` : `Mín. ${prefix ?? ''} ${formatAmount(min)}`)}
            </span>
          </div>
        )}
      </div>

      {open && createPortal(
        <div
          ref={popoverRef}
          id={popoverId}
          role="listbox"
          style={{
            position: 'fixed',
            top: popoverPos.top,
            left: popoverPos.left,
            minWidth: popoverPos.width,
            visibility: popoverPos.visible ? 'visible' : 'hidden',
          }}
          className="z-[9999] flex flex-col gap-0.5 rounded-lg border border-muted bg-surface-0 p-1.5 shadow-lg"
        >
          {options.length === 1 && (
            <div className="mb-0.5 flex items-start gap-1.5 rounded-t-md border-b border-muted bg-info-bg p-2 text-[11.5px] leading-snug text-secondary">
              <Info size={13} className="mt-px shrink-0 text-info-hover" />
              <span>Este elemento solo tiene un tipo configurado.</span>
            </div>
          )}
          {options.map((option) => {
            const isSelected = option.id === selectedOptionId;
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => handleOptionClick(option)}
                className={[
                  'flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-ui-sm',
                  isSelected ? 'bg-brand-light font-semibold text-brand' : 'text-secondary hover:bg-surface-hover',
                ].join(' ')}
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${option.dotColorClass ?? 'bg-neutral-60'}`} />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
